import { useEffect, useState } from 'react';
import { FileWarning, X } from 'lucide-react';
import { listOpen, abort } from '../../domain/services/upload/upload.session.service';
import { fingerprintOf } from '../../domain/services/upload/upload.fingerprint';
import { uploadEngine } from '../../domain/services/upload/upload.engine';
import { engineState } from '../../domain/services/upload/upload.engine.state';
import { inflightRegistry } from '../../domain/services/upload/upload.inflight.registry';

const ICON_SIZE = 16;

const COMPLETED = 'completed';

const RESUMABLE_TARGETS = Object.freeze(['requestDocument', 'customerDocument', 'faciliterInvoice']);

const MISMATCH_MESSAGE = 'Este não é o mesmo arquivo. Descartar o envio anterior?';

const UPLOADING = 'uploading';

const resumeThroughEngine = (session, file, onState) => uploadEngine.upload({
    file, targetKind: session.targetKind, target: session.target, resumeSessionId: session.sessionId, onState,
});

const notSentHere = (session) => !inflightRegistry.has(session.sessionId);

const isResumable = (session) => RESUMABLE_TARGETS.includes(session.targetKind);

const pendingText = (session) => (isResumable(session)
    ? `envio de ${session.fileName} pela metade, selecione o mesmo arquivo para continuar`
    : `envio de ${session.fileName} pela metade; envie de novo pela tela ou descarte`);

const resultText = (session, outcome) => (outcome && outcome.status === COMPLETED
    ? `${session.fileName} enviado`
    : (outcome && outcome.message) || `O envio de ${session.fileName} parou: selecione o mesmo arquivo para continuar de onde parou.`);

const lineText = (session, result, percent) => {
    if (result) return result;
    if (percent !== undefined) return `enviando ${session.fileName}: ${percent}%`;
    return pendingText(session);
};

const SessionLine = ({ session, result, percent, onPick, onDiscard }) => (
    <li className="flex flex-wrap items-center justify-between gap-2 py-1">
        <span>{lineText(session, result, percent)}</span>
        {!result && percent === undefined && isResumable(session) && (
            <input
                type="file"
                aria-label={`selecionar o arquivo de ${session.fileName}`}
                className="text-xs"
                onChange={(event) => event.target.files[0] && onPick(session, event.target.files[0])}
            />
        )}
        {!result && percent === undefined && (
            <button type="button" aria-label={`descartar o envio de ${session.fileName}`} className="text-xs font-medium text-red-700 hover:underline" onClick={() => onDiscard(session)}>
                descartar
            </button>
        )}
    </li>
);

const NoticeHeader = ({ onClose }) => (
    <div className="flex items-center justify-between">
        <FileWarning size={ICON_SIZE} />
        <button type="button" aria-label="fechar o aviso de envios pela metade" className="text-amber-900 hover:text-amber-700" onClick={onClose}>
            <X size={ICON_SIZE} />
        </button>
    </div>
);

const MismatchQuestion = ({ onDiscard, onKeep }) => (
    <div className="mt-2 flex items-center gap-2">
        <span>{MISMATCH_MESSAGE}</span>
        <button type="button" className="font-medium text-red-700 hover:underline" onClick={onDiscard}>sim, descartar</button>
        <button type="button" className="font-medium text-gray-700 hover:underline" onClick={onKeep}>não</button>
    </div>
);

const useOpenSessions = () => {
    const [sessions, setSessions] = useState([]);
    useEffect(() => {
        if (engineState.isKnownOff()) return;
        listOpen().then(list => setSessions((list || []).filter(notSentHere)), () => setSessions([]));
    }, []);
    const drop = (sessionId) => setSessions(current => current.filter(session => session.sessionId !== sessionId));
    return { sessions, drop };
};

export default function ResumeUploadsNotice({ onResume = resumeThroughEngine, fingerprints = {} }) {
    const { sessions, drop } = useOpenSessions();
    const [results, setResults] = useState({});
    const [mismatch, setMismatch] = useState(null);
    const [closed, setClosed] = useState(false);
    const [progress, setProgress] = useState({});

    const settle = (session, outcome) => setResults(current => ({ ...current, [session.sessionId]: resultText(session, outcome) }));

    const track = (session) => (state) => {
        if (state && state.state === UPLOADING) setProgress(current => ({ ...current, [session.sessionId]: state.percent }));
    };

    const pick = async (session, file) => {
        if ((await fingerprintOf(file)) !== (session.fingerprint || fingerprints[session.sessionId])) return setMismatch(session);
        return settle(session, await Promise.resolve(onResume(session, file, track(session))).catch(error => ({ message: error && error.message })));
    };

    const discard = async (session) => {
        await abort(session.sessionId);
        drop(session.sessionId);
        setMismatch(null);
    };

    if (!sessions.length || closed) return null;

    return (
        <div role="region" aria-label="Envios pela metade" className="fixed left-4 right-4 top-20 z-[10001] sm:left-auto sm:max-w-md rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 shadow-lg">
            <NoticeHeader onClose={() => setClosed(true)} />
            <ul>{sessions.map(session => <SessionLine key={session.sessionId} session={session} result={results[session.sessionId]} percent={results[session.sessionId] ? undefined : progress[session.sessionId]} onPick={pick} onDiscard={discard} />)}</ul>
            {mismatch && <MismatchQuestion onDiscard={() => discard(mismatch)} onKeep={() => setMismatch(null)} />}
        </div>
    );
}
